import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import DealNavigation from "../../components/DealNavigation";
import BuyerDetailModal from "../../components/BuyerDetailModal";
import ShipperDetailModal from "../../components/ShipperDetailModal";
import FollowupModal from "../../components/FollowupModal";
import FixationModal from "../../components/FixationModal";
import DealInfoPanel from "../../components/DealInfoPanel";
import { buildFixationPricingDefaults } from "../../utils/fixationDefaults";
import { showToast } from "../../utils/toast";

const MAX_ATTACHMENT_SIZE = 15 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set(["pdf", "jpg", "jpeg", "png", "gif", "webp"]);

const AdvancePayment = () => {

  const { dealId } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState({});
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDealInfo, setShowDealInfo] = useState(true);
  const [editId, setEditId] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef(null);

  const [showBuyer, setShowBuyer] = useState(false);
  const [showShipper, setShowShipper] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [showFixation, setShowFixation] = useState(false);
  const fixationPricingDefaults = useMemo(() => buildFixationPricingDefaults(deal), [deal]);
  const minimumAdvanceDate = useMemo(() => {
    const dealDate = String(deal.DealDate || "").split("T")[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dealDate)) return "";
    const nextDate = new Date(`${dealDate}T00:00:00Z`);
    nextDate.setUTCDate(nextDate.getUTCDate() + 1);
    return nextDate.toISOString().split("T")[0];
  }, [deal.DealDate]);

  const handleEdit = (row) => {

    setEditId(row.AdvPayId);

    setFormData({
      advanceDate: row.AdvPaymentDate.split("T")[0],
      advanceAmount: row.AdvPaymentRs,
      totalContainer: row.TotalContainer,
      ttRefNo: row.PaymentRefNo || "",
      remarks: row.AdvRemark || ""
    });

  };
  const [formData, setFormData] = useState({
    advanceDate: "",
    advanceAmount: "",
    totalContainer: "",
    ttRefNo: "",
    remarks: ""
  });

  useEffect(() => {
    if (dealId) {
      fetchData();
    }
  }, [dealId]);

  const fetchData = async () => {

    try {

      setLoading(true);

      const dealRes = await axios.get(`/container-release/deal/${dealId}`);
      setDeal(dealRes.data || {});

      const res = await axios.get(`/advance-payment/${dealId}`);
      setDeal({ ...(dealRes.data || {}), DueDays: res.data.deal?.DueDays || 0 });
      setPayments(res.data.payments); // payment grid

    }
    catch (err) {
      console.error(err);
    }

    setLoading(false);

  };
  const handleSave = async () => {

    try {
      const isEditing = Boolean(editId);

      // validations
      const dealDate = String(deal.DealDate || "").split("T")[0];
      if (!formData.advanceDate) {
        alert("Advance Payment Date is required");
        return;
      }
      if (dealDate && formData.advanceDate <= dealDate) {
        alert("Advance Payment Date must be after Deal Date");
        return;
      }
      if (new Date(formData.advanceDate) < new Date(deal.SCRDate)) {
        alert("Advance Payment Date cannot be before Sales Contract Date");
        return;
      }

      if (Number(formData.totalContainer) > Number(deal.NoOfContainer)) {
        alert("Container exceeds allowed quantity");
        return;
      }

      if (Number(formData.advanceAmount) <= 0) {
        alert("Advance amount must be greater than zero");
        return;
      }

      // API call
      const response = await axios.post("/advance-payment", {
        dealId,
        paymentId: editId,
        advanceDate: formData.advanceDate,
        advanceAmount: formData.advanceAmount,
        totalContainer: formData.totalContainer,
        ttRefNo: formData.ttRefNo,
        remarks: formData.remarks
      });

      setEditId(null);

      // reset form
      setFormData({
        advanceDate: "",
        advanceAmount: "",
        totalContainer: "",
        ttRefNo: "",
        remarks: ""
      });
      alert(isEditing ? "Advance payment updated successfully" : "Advance payment saved successfully");
      if (response.data?.nextStage === "shipment") {
        navigate(`/transaction/shipment-schedule/${dealId}`);
      } else {
        await fetchData();
      }

    }
    catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        alert(err.response.data.message);
      } else {
        alert("Something went wrong while saving payment");
      }
    }

  };

  const uploadAttachments = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const unsupported = files.find((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      return !ALLOWED_ATTACHMENT_EXTENSIONS.has(extension);
    });
    if (unsupported) {
      showToast("Advance Payment attachments must be PDF or image files.");
      return;
    }
    const oversized = files.find((file) => file.size > MAX_ATTACHMENT_SIZE);
    if (oversized) {
      showToast("Each attachment must be 15 MB or smaller.");
      return;
    }

    const fields = new FormData();
    files.forEach((file) => fields.append("files", file));
    const reference = formData.ttRefNo ? ` - T.T Ref ${formData.ttRefNo}` : "";
    fields.append("remark", `Uploaded from Advance Payment${reference}`);

    try {
      setUploadingAttachment(true);
      await axios.post(`/sales-contract/attachments/deal/${dealId}/advance-payment`, fields);
      showToast(`${files.length} Advance Payment attachment${files.length === 1 ? "" : "s"} saved. View in the Attachment page.`);
    } catch (err) {
      showToast(err.response?.data?.message || "Unable to save Advance Payment attachment.");
    } finally {
      setUploadingAttachment(false);
    }
  };
  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <>
            <DealNavigation
                dealId={dealId}
                openBuyer={() => setShowBuyer(true)}
                openShipper={() => setShowShipper(true)}
                openFixation={() => setShowFixation(true)}
                openFollowup={() => setShowFollowup(true)}
            />
    <div className="container-fluid mt-3">

      {/* ===== HEADER BADGE ===== */}
      <div className="mb-2">
        <span className="badge bg-success fs-6 px-3 py-2">
          Advance Payment
        </span>
      </div>


      <DealInfoPanel
        deal={deal}
        show={showDealInfo}
        onToggle={() => setShowDealInfo(!showDealInfo)}
      />

      {/* ================= VIEW DEAL INFO ================= */}
      <div className="border rounded bg-white mb-3 d-none">

        <div
          className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          onClick={() => setShowDealInfo(!showDealInfo)}
        >
          View Deal Info
          <span>{showDealInfo ? "▲" : "▼"}</span>
        </div>

        {showDealInfo && (
          <div className="p-3">
            {/* Row 1 */}
            <div className="row g-3 mb-2">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Deal No - Date</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={`${deal.DealID} - ${deal.DealDate?.split("T")[0]}`}
                  disabled
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">Status</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={deal.Status || "DOC RELEASE"}
                  disabled
                />
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">S.C No - Date</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={
                    deal.SCRNo
                      ? `${deal.SCRNo}${deal.SCRDate ? ` - ${deal.SCRDate.split("T")[0]}` : ""}`
                      : ""
                  }
                  disabled
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="row g-3 mb-2">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Buyer Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={deal.BuyerName || ""}
                  disabled
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Shipper Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={deal.ShipperName || ""}
                  disabled
                />
              </div>
            </div>

            {/* Row 3 */}
            <div className="row g-3 mb-2">
              <div className="col-md-2">
                <label className="form-label fw-semibold">Shipment Period</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={deal.ShipmentDays || ""}
                  disabled
                />
              </div>

              <div className="col-md-2">
                <label className="form-label fw-semibold">No. Of Container</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={deal.NoOfContainer || ""}
                  disabled
                />
              </div>

              <div className="col-md-2">
                <label className="form-label fw-semibold">Total Qty</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={deal.TotalQty || ""}
                  disabled
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Material Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={deal.MaterialName || ""}
                  disabled
                />
              </div>
            </div>
          </div>
        )}

      </div>


      {/* ================= EXISTING ADVANCE PAYMENT GRID ================= */}
      <div className="border rounded bg-white mb-3">

        <div className="px-3 py-2 border-bottom fw-bold text-muted">
          Advance Payment History
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light text-center">
              <tr>
                <th>Date</th>
                <th>Advance Payment</th>
                <th>Reference No</th>
                <th>Total Container</th>
                <th>Remark</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Records Found
                  </td>
                </tr>
              ) : (
                payments.map((row) => (
                  <tr key={row.AdvPayId}>
                    <td>{row.AdvPaymentDate?.split("T")[0]}</td>
                    <td>{row.AdvPaymentRs}</td>
                    <td>{row.PaymentRefNo}</td>
                    <td>{row.TotalContainer}</td>
                    <td>{row.AdvRemark}</td>
                    <td>
                      <button
                        className="btn btn-link btn-sm"
                        onClick={() => handleEdit(row)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>

      </div>


      {/* ================= ADVANCE PAYMENT ENTRY FORM ================= */}
      <div className="border rounded bg-white p-4 mb-3">

        <div className="row g-3">
          <div className="col-md-3">
            <label>Due Days</label>
            <input className="form-control" value={deal.DueDays || 0} disabled />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold">Advance Payment Date *</label>
            <input
              type="date"
              className="form-control form-control-sm"
              min={minimumAdvanceDate}
              value={formData.advanceDate}
              onChange={(e) =>
                setFormData({ ...formData, advanceDate: e.target.value })
              }
            />
          </div>

          <div className="col-md-3">
            <label className="form-label fw-bold">Advance Amount *</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={formData.advanceAmount}
              onChange={(e) =>
                setFormData({ ...formData, advanceAmount: e.target.value })
              }
            />
          </div>

          <div className="col-md-2">
            <label className="form-label fw-bold">Total Container *</label>
            <input
              type="number"
              className="form-control form-control-sm"
              value={formData.totalContainer}
              onChange={(e) =>
                setFormData({ ...formData, totalContainer: e.target.value })
              }
            />
          </div>

          <div className="col-md-4">
            <label className="form-label fw-bold">T.T Ref No</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={formData.ttRefNo}
              onChange={(e) =>
                setFormData({ ...formData, ttRefNo: e.target.value })
              }
            />
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <input
              ref={attachmentInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/gif,image/webp,.pdf,.jpg,.jpeg,.png,.gif,.webp"
              multiple
              className="d-none"
              onChange={uploadAttachments}
            />
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => attachmentInputRef.current?.click()}
              disabled={uploadingAttachment}
            >
              {uploadingAttachment ? "Uploading..." : "Attach PDF / Image"}
            </button>
          </div>

          <div className="col-md-12">
            <label className="form-label fw-bold">Remarks</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={formData.remarks}
              onChange={(e) =>
                setFormData({ ...formData, remarks: e.target.value })
              }
            />
          </div>

        </div>

      </div>


      {/* ================= ACTION BUTTONS ================= */}
      <div className="text-center mb-4">
        <button
          className="btn btn-success btn-sm me-2"
          onClick={handleSave}
        >
          Save
        </button>

        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
      </div>

    </div>
     <BuyerDetailModal
                show={showBuyer}
                dealId={dealId}
                onClose={() => setShowBuyer(false)}
            />
            <ShipperDetailModal
                show={showShipper}
                dealId={dealId}
                onClose={() => setShowShipper(false)}
            />
            <FixationModal
                show={showFixation}
                dealId={dealId}
                totalQty={deal.TotalQty}
                dealCurrency={deal.DealCurrency}
                dealPricingDefaults={fixationPricingDefaults}
                onClose={() => setShowFixation(false)}
            />
            <FollowupModal
                show={showFollowup}
                dealId={dealId}
                onClose={() => setShowFollowup(false)}
            />
        </>
  );
};

export default AdvancePayment;
